/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Injectable } from '@angular/core';

@Injectable()
export class LbRbacService {
    /**
     * A boolean to determine if the user is admin or not.
     */
    public isSuperUser: boolean = false;

    /**
     * Page lookup by URL.
     * @param url
     */
    public getPathAndMethodForUrl(url: string): { paths: string | string[], method: string } {
        const urls: any = this.getLookupObject();

        return { paths: urls[url]?.path, method: urls[url]?.method };
    }

    /**
     * Look up object
     */
    public getLookupObject(): any {
        return {
            // Overview
            '/lb/overview': { path: '/' },
            '/lb/overview/details': { path: '/system-stats', method: 'GET' },
            // Local configuration
            '/lb/local-configuration/sys-date-time': {
                path: ['/system/datetime', '/system/datetime/timezones'],
                method: 'GET'
            },
            '/lb/local-configuration/user-interface': { path: '/system/userinterface', method: 'GET' },
            '/lb/local-configuration/phys-adv-conf': {
                path: ['/system/hostname', '/system/proxy', '/system/console', '/system/conntrack', '/system/keymap', '/system/mail'],
                method: 'GET'
            },
            '/lb/local-configuration/snmp': { path: '/snmp', method: 'GET' },
            '/lb/local-configuration/snmp/config': { path: '/snmp', method: 'PUT' },
            '/lb/local-configuration/snmp/user': { path: '/snmp/users', method: 'POST' },
            '/lb/local-configuration/snmp/user/edit': { path: '/snmp/users/:id', method: 'PUT' },
            '/lb/local-configuration/licence': { path: '/' },
            '/lb/local-configuration/execute': { path: '/system/execute', method: 'PUT' },
            '/lb/local-configuration/collectors/prometheus': { path: '/data-services', method: 'GET' },
            '/lb/local-configuration/portal-management': {
                path: ['/portal/status', '/ipgroups', '/sips', '/portal/sidecar'],
                method: 'GET'
            },
            // Network
            '/lb/network/interfaces': { path: '/interfaces', method: 'GET' },
            '/lb/network/interfaces/edit-interface': { path: '/interfaces/:id', method: 'PUT' },
            '/lb/network/interfaces/add-vlan': { path: '/interfaces', method: 'POST' },
            '/lb/network/interfaces/edit-vlan': { path: '/interfaces/:id', method: 'PUT' },
            '/lb/network/interfaces/add-bond': { path: '/interfaces/:id', method: 'PUT' },
            '/lb/network/interfaces/break-bond': { path: '/interfaces/:id', method: 'PUT' },
            '/lb/network/static/ips': { path: ['/sips', '/ipgroups', '/interfaces'], method: 'GET' },
            '/lb/network/static/ips/add': { path: '/sips', method: 'POST' },
            '/lb/network/static/ips/edit': { path: '/sips/:id', method: 'PUT' },
            '/lb/network/ip-groups': { path: ['/ipgroups', '/interfaces'], method: 'GET' },
            '/lb/network/ip-groups/add': { path: '/ipgroups', method: 'POST' },
            '/lb/network/ip-groups/edit': { path: '/ipgroups/:id', method: 'PUT' },
            '/lb/network/floating/ips': { path: ['/fips', '/ipgroups', '/interfaces'], method: 'GET' },
            '/lb/network/floating/ips/add': { path: '/fips', method: 'POST' },
            '/lb/network/floating/ips/edit': {
                path: ['/fips/:id', '/fips/enable', '/fips/disable'],
                method: 'PUT'
            },
            '/lb/network/regions': { path: '/regions', method: 'GET' },
            '/lb/network/regions/': { path: '/regions/:id', method: 'GET' },
            '/lb/network/regions/add': { path: '/regions', method: 'POST' },
            '/lb/network/regions/edit': { path: '/regions/:id', method: 'PUT' },
            // Cluster configuration
            '/lb/cluster-configuration/manage-services': {
                path: ['/haproxy/frontends', '/haproxy/backends', '/haproxy/rips', '/haproxy/acls', '/fips'],
                method: 'GET'
            },
            '/lb/cluster-configuration/manage-services/service-details': {
                path: ['/haproxy/frontends', '/haproxy/backends', '/haproxy/rips', '/fips'],
                method: 'GET'
            },
            '/lb/cluster-configuration/manage-acls': {
                path: ['/haproxy/frontends', '/haproxy/backends', '/haproxy/acls'],
                method: 'GET'
            },
            '/lb/cluster-configuration/manage-acls/acls-details': {
                path: ['/haproxy/frontends', '/haproxy/backends', '/haproxy/acls'],
                method: 'GET'
            },
            '/lb/cluster-configuration/advanced-services': { path: ['/haproxy/global'], method: 'PUT' },
            '/lb/cluster-configuration/ssl-certificate': {
                path: ['/certificates/cas', '/certificates/certs', '/certificates/csrs', '/certificates/crls'],
                method: 'GET',
                type: 'OR'
            },
            '/lb/cluster-configuration/ssl-certificate/cert/add': {
                path: ['/certificates/certs', '/certificates/csrs'],
                method: 'POST'
            },
            '/lb/cluster-configuration/ssl-certificate/cert/edit': { path: '/certificates/certs/:id', method: 'PUT' },
            '/lb/cluster-configuration/ssl-certificate/cert/upload': { path: '/certificates/certs', method: 'POST' },
            '/lb/cluster-configuration/ssl-certificate/cert/apply-csr': {
                path: '/certificates/csrs/:id/apply',
                method: 'PUT'
            },
            '/lb/cluster-configuration/ssl-certificate/csr/new': { path: ['/certificates/csrs'], method: 'POST' },
            '/lb/cluster-configuration/ssl-certificate/csr/edit': { path: ['/certificates/csrs/:id'], method: 'PUT' },
            '/lb/cluster-configuration/ssl-certificate/self-signed/new': { path: ['/certificates/csrs'], method: 'POST' },
            '/lb/cluster-configuration/ssl-certificate/ca/new': { path: ['/certificates/cas'], method: 'POST' },
            '/lb/cluster-configuration/ssl-certificate/ca/edit': { path: ['/certificates/cas/:id'], method: 'PUT' },
            '/lb/cluster-configuration/ssl-certificate/crl/new': { path: ['/certificates/crls'], method: 'POST' },
            '/lb/cluster-configuration/ssl-certificate/crl/edit': { path: ['/certificates/csrs/:id'], method: 'PUT' },
            '/lb/cluster-configuration/cipher-lists': { path: ['/ciphers/lists'], method: 'GET' },
            '/lb/cluster-configuration/cipher-lists/new': { path: ['/ciphers/lists'], method: 'POST' },
            '/lb/cluster-configuration/cipher-lists/duplicate': { path: ['/ciphers/lists'], method: 'POST' },
            '/lb/cluster-configuration/cipher-lists/edit': { path: ['/ciphers/lists/:id'], method: 'PUT' },
            '/lb/cluster-configuration/ha-config': { path: ['/status'], method: 'GET' },
            '/lb/cluster-configuration/ha-config/add': { path: ['/cluster/join'], method: 'POST' },
            '/lb/cluster-configuration/ha-config/restore': { path: ['/cluster/restore'], method: 'POST' },
            '/lb/cluster-configuration/pulse-config': { path: ['/status'], method: 'GET' },
            '/lb/cluster-configuration/gslb': {
                path: ['/gslb/stats/generic-state', '/gslb/globalnames', '/gslb/members', '/gslb/pools', '/gslb/topologies'],
                method: 'GET'
            },
            '/lb/cluster-configuration/gslb/global-names/add': { path: ['/gslb/globalnames'], method: 'POST' },
            '/lb/cluster-configuration/gslb/global-names/edit': { path: ['/gslb/globalnames/:id'], method: 'PUT' },
            '/lb/cluster-configuration/gslb/members/add': { path: ['/gslb/members'], method: 'POST' },
            '/lb/cluster-configuration/gslb/members/edit': { path: ['/gslb/members/:id'], method: 'PUT' },
            '/lb/cluster-configuration/gslb/pools/add': { path: ['/gslb/pools'], method: 'POST' },
            '/lb/cluster-configuration/gslb/pools/edit': { path: ['/gslb/pools/:id'], method: 'PUT' },
            '/lb/cluster-configuration/gslb/topologies/add': { path: ['/gslb/topologies'], method: 'POST' },
            '/lb/cluster-configuration/gslb/topologies/edit': { path: ['/gslb/topologies/:id'], method: 'PUT' },
            '/lb/cluster-configuration/healthchecks': { path: '/healthchecks', method: 'GET' },
            '/lb/cluster-configuration/healthchecks/add': { path: ['/healthchecks'], method: 'POST' },
            '/lb/cluster-configuration/healthchecks/duplicate': { path: ['/healthchecks'], method: 'POST' },
            '/lb/cluster-configuration/healthchecks/edit': { path: ['/healthchecks/:id'], method: 'PUT' },
            '/lb/cluster-configuration/healthchecks/view': { path: ['/healthchecks/:id'], method: 'GET' },
            // New service
            '/lb/service/add': { path: '/' },
            '/lb/service/edit': { path: '/' },
            // ACLs
            '/lb/acl/add': { path: '/' },
            '/lb/acl/edit': { path: '/' },
            // Maintenance
            '/lb/maintenance/backup-restore': { path: '/backup/list', method: 'GET' },
            '/lb/maintenance/backup-restore/restore': { path: '/backup/restore', method: 'POST' },
            '/lb/maintenance/backup-restore/upload': { path: '/backup/upload', method: 'POST' },
            '/lb/maintenance/services': { path: '/services', method: 'GET' },
            '/lb/maintenance/fallback-pages': { path: '/fallback', method: 'GET' },
            '/lb/maintenance/fallback-pages/add': { path: '/fallback', method: 'POST' },
            '/lb/maintenance/fallback-pages/edit': { path: '/fallback/:id', method: 'PUT' },
            '/lb/maintenance/software-update': { path: '/system/update', method: 'PUT' },
            '/lb/maintenance/users/list': {
                path: ['/rbac/users', '/rbac/roles', '/rbac/groups'],
                method: 'GET'
            },
            '/lb/maintenance/users/user/add': { path: '/rbac/users', method: 'POST' },
            '/lb/maintenance/users/user/edit': { path: '/rbac/users/:id', method: 'PUT' },
            '/lb/maintenance/users/role/add': { path: '/rbac/roles', method: 'POST' },
            '/lb/maintenance/users/role/edit': { path: '/rbac/roles/:id', method: 'PUT' },
            '/lb/maintenance/users/group/add': { path: '/rbac/groups', method: 'POST' },
            '/lb/maintenance/users/group/edit': { path: '/rbac/groups/:id', method: 'PUT' },

            // View configuration
            '/lb/view-configuration': { path: '/configs', method: 'GET' }, // Top level only

            // Reports
            '/lb/reports/gslb-reports': { path: '/' },
            '/lb/reports/gslb-reports/healthcheck': { path: '/gslb/stats/generic-state', method: 'GET' },
            '/lb/reports/gslb-reports/distributionstate': { path: '/gslb/stats/ppdns-state', method: 'GET' },

            // Logs
            '/lb/view-logs/load-balancer': { path: ['/logs', '/logs/services'], method: 'GET' },

            // Notifications
            '/lb/notifications': { path: '/' },

            // Support
            '/lb/support': { path: '/' },
            '/lb/support/download': {
                path: ['/support/status', '/support/download/:option', '/support/download/file/:filename'],
                method: 'GET'
            },
            '/lb/support/contact-us': { path: '/' },
        };
    }

    /**
     * Strip appliance id from url.
     * @param url
     * @private
     */
    public stripApplianceIdFromUrl(url: string): string {
        const lbUser: any = JSON.parse(localStorage.getItem('lbUser'));
        const applianceId: string = lbUser?.applianceId;

        return url.replace(`/${applianceId}`, '');
    }

    /**
     * Excluded params by url.
     */
    public getExcludedParams(): string[] {
        return ['json', 'l7', 'net-conf', 'routing-table', 'snmp-conf', 'healthcheck', 'distributionstate'];
    }
}
